import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ChannelStatusChip, ChannelSparkline, DeltaText, TankFill } from "./ChannelVisuals";
import { formatChannelValue } from "./channelUtils";
import { statusTone } from "./channelTheme";

const cellSx = ({ palette, functions: { rgba } }) => ({
  borderBottom: `1px solid ${rgba(palette.text.main, 0.08)}`,
  py: 1,
  px: 1.5,
  whiteSpace: "nowrap",
});

const headSx = (theme) => ({
  ...cellSx(theme),
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: theme.palette.text.main,
  opacity: 0.7,
});

const COLUMNS = ["Channel", "Type", "Value", "Level", "24h", "Trend", "Status"];

// Header row lives inside TableBody: Material Dashboard's TableHead overrides break layout.
export default function ChannelTable({ channels = [], selectedKey, onSelect }) {
  return (
    <MDBox sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 720 }}>
        <TableBody>
          <TableRow>
            {COLUMNS.map((c) => (
              <TableCell key={c} sx={headSx} component="th" scope="col">
                {c}
              </TableCell>
            ))}
          </TableRow>
          {channels.map((c) => {
            const selected = String(selectedKey) === c.key;
            return (
              <TableRow
                key={c.key}
                hover
                tabIndex={0}
                aria-selected={selected}
                onClick={() => onSelect?.(c.number)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect?.(c.number);
                  }
                }}
                sx={({ palette, functions: { rgba } }) => ({
                  cursor: "pointer",
                  backgroundColor: selected ? rgba(palette.info.main, 0.1) : "transparent",
                  "&:focus-visible": { outline: `2px solid ${palette.info.main}`, outlineOffset: -2 },
                })}
              >
                <TableCell sx={cellSx}>
                  <MDTypography variant="button" fontWeight="bold">
                    {c.name}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" sx={{ ml: 0.75, opacity: 0.7 }}>
                    #{c.number}
                  </MDTypography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <MDTypography variant="caption" color="text">
                    <Icon className={c.icon} sx={{ fontSize: "0.75rem !important", mr: 0.75, verticalAlign: "middle" }} />
                    {c.kindLabel}
                    {c.role ? ` · ${c.role}` : ""}
                  </MDTypography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <MDTypography variant="button" fontWeight="bold">
                    {formatChannelValue(c.value, c.unit, c.kind)}
                  </MDTypography>
                  {c.volume !== null && (
                    <MDTypography variant="caption" color="text" sx={{ ml: 0.75 }}>
                      {formatChannelValue(c.volume, c.volumeUnit)}
                    </MDTypography>
                  )}
                </TableCell>
                <TableCell sx={(t) => ({ ...cellSx(t), minWidth: 110 })}>
                  {c.kind === "level" ? <TankFill channel={c} height={6} /> : "—"}
                </TableCell>
                <TableCell sx={cellSx}>
                  <DeltaText delta={c.delta24} unit={c.unit} suffix="" />
                </TableCell>
                <TableCell sx={(t) => ({ ...cellSx(t), width: 120 })}>
                  <ChannelSparkline points={c.points24} height={22} tone={c.status.level === "ok" ? "info" : statusTone(c)} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <ChannelStatusChip channel={c} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </MDBox>
  );
}
